import { BindingEngine } from "aurelia-binding";
import { transient } from "aurelia-dependency-injection";
import { BindingSignaler } from "aurelia-templating-resources";
import { Claims } from "../../../../../../constants/authentication/claims";
import { InspectorReportQuestionModel, InspectorReportSectionModel, RightshipInspectionAnswerClassificationsEnum } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { RightShipInspectionsService } from "../../../../../../services/rightship-inspections/rightship-inspections.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { UnitOfWorkObserver } from "../../../../../../utilities/unit-of-work-observer";



@transient()
export class EditInspectorReportDialog {

    section: InspectorReportSectionModel;
    canEditInspectionReportLevel2:boolean;
    isQuestionNonConformity: {[key: string]: boolean} = {};
    rightShipInspectionId: number;
    title: string;
    showMoreText: {[key: string]: boolean} = {};
    public editInspectorReportObserver: UnitOfWorkObserver;
    private _originalSection:InspectorReportSectionModel = null;

    onSave: () => void;

    constructor(private dialogClientService: DialogClientService, 
                private engine: BindingEngine,
                private rightShipInspectionService:RightShipInspectionsService,
                private notificationService: NotificationClientService,
                private bindingSignaler: BindingSignaler,
                userClamiService:UserClaimsService){
                    this.canEditInspectionReportLevel2= userClamiService.hasPermission(Claims.CanEditInspectionReportLevel2);
                }
    
    activate(model: { rightShipInspectionId: number, section: InspectorReportSectionModel, title: string, onSave: () => void} ) {
        this.section = CloneHelper.cloneObject(model.section);
        this._originalSection = CloneHelper.cloneObject(model.section);
        this.rightShipInspectionId = model.rightShipInspectionId;
        this.title = model.title;
        this.onSave = model.onSave;
        this.setNonConfromities();
        this.initializeUow();
    }

    setNonConfromities()
    {
        if(this.section && this.section.questions)
        {
            this.section.questions
            .forEach((q, i) => {
                this.isQuestionNonConformity[i] = q.rightshipInspectionAnswerClassificationId == RightshipInspectionAnswerClassificationsEnum.No;
            });
        }
    }
    
    sectionHasComments(section: InspectorReportSectionModel): boolean {
        return section.sectionNumber != "0" && section.sectionNumber != "1";
    }

    sectionHasAdditionalComments(section: InspectorReportSectionModel): boolean {
        return section.sectionNumber == "1";
    }

    close() {
        this.dialogClientService.hide();
    }

    initializeUow() {
        if (this.editInspectorReportObserver)
            this.editInspectorReportObserver.dispose();

        this.editInspectorReportObserver = UnitOfWorkObserver.getTrackingInstance(this.engine,{inspectionReports:this.section.questions});
        this.editInspectorReportObserver.startTracking();
    }

    getLongText(fullText: string, key: string)
    {
        if(this.showMoreText && Object.keys(this.showMoreText).indexOf(key) > -1)
        {
            if(this.showMoreText[key])
                return fullText;
        }
        return fullText? fullText.substring(0, 100) + (fullText.length > 100? '...': ''): '';
    }

    toggleReadMore(key: string)
    {
        if(!this.showMoreText){
            this.showMoreText = {};
        }

        this.showMoreText[key] = Object.keys(this.showMoreText).indexOf(key) > -1? !this.showMoreText[key]: true;
        this.bindingSignaler.signal('read-more-toggled');
    }

    async save(){
        const changes = this.editInspectorReportObserver.getChanges();

        if (Object.keys(changes).length === 0){
            this.notificationService.show("No changes to save", "", NotificationType.Info);
            this.dialogClientService.hide(true);
            return;
        }
        const saveRequest = {
            changes: changes,
            id:this.section.rightshipInspectionFormSectionId,
            rightshipInspectionId: this.rightShipInspectionId           
        }
        try{
            await this.rightShipInspectionService.updateRightShipInspectionReports(saveRequest);
            if(this.onSave)
            {
                this.onSave();
            }
            this.notificationService.show('','Inspection Reports Updated Successfully',NotificationType.Success);
            this.dialogClientService.hide(true);
        }
        catch (e){ 
            this.notificationService.show("Failed to save", "", NotificationType.Error);
        }
    }

    detached(){
     if (this.editInspectorReportObserver)
            this.editInspectorReportObserver.dispose();
    }

    getOutComeValue(checked: boolean, question:InspectorReportQuestionModel,value:string){
        if(!checked) return;
        const wasNonConformity = question.rightshipInspectionAnswerClassificationId == RightshipInspectionAnswerClassificationsEnum.No;
        const index = this.section.questions.indexOf(question);
        question.rightshipInspectionAnswerClassificationId = this.getclassificationId(index, value);
        if(!wasNonConformity && question.rightshipInspectionAnswerClassificationId == RightshipInspectionAnswerClassificationsEnum.No)
        {
            if(!question.rightShipInspectionDeficiency)
            {
                question.rightShipInspectionDeficiency = { rightShipInspectionDeficiencyId: 0, rightShipInspectionId: this.rightShipInspectionId, isActive: true, vetSuperModifiedOutcome: false };
            }
            else
            {
                question.rightShipInspectionDeficiency.isActive = true;
            }
        }
        else if(wasNonConformity && question.rightshipInspectionAnswerClassificationId != RightshipInspectionAnswerClassificationsEnum.No)
        {
            if(question.rightShipInspectionDeficiency.rightShipInspectionDeficiencyId > 0)
            {
                question.rightShipInspectionDeficiency.isActive = false;
            }
            else
            {
                question.rightShipInspectionDeficiency = null;
            }
        }
        question.isOverriden = true;
    }

    updateOverridenFlag(question:InspectorReportQuestionModel, propertyPath: string = null){
        // I'm doing this to check if the new value is unchanged, I'll ignore setting the isOverridden to true
        if(propertyPath && !this.propertyChanged(question, propertyPath)) return;
        question.isOverriden = true;
    }
    
    propertyChanged(question:InspectorReportQuestionModel, propertyPath: string)
    {
            let newValue: any = question;
            let originalValue: any = this._originalSection.questions.find(x=>x['id'] == question['id']);
            const props = propertyPath.split(".");
            props.forEach(p => {
                originalValue = originalValue && Object.keys(originalValue).indexOf(p) > -1? originalValue[p]: null;
                newValue = newValue[p];
            });
    
            return originalValue != newValue;
    }
    getclassificationId(index: number, value: string): number{
        value= value.toLocaleLowerCase();
        this.isQuestionNonConformity[index]=false;
        let classifcationId:number;
        if(value ==='no'){
            classifcationId = RightshipInspectionAnswerClassificationsEnum.No;

            this.isQuestionNonConformity[index] = true;
        }
       else if(value === 'n/a'){
            classifcationId = RightshipInspectionAnswerClassificationsEnum.Na;
        }
       else if(value==='yes'){
            classifcationId = RightshipInspectionAnswerClassificationsEnum.Yes;
        }
       else{
            classifcationId = RightshipInspectionAnswerClassificationsEnum.Nv;
        }
        return classifcationId;
    }

    inspectorCommnetsChanged(value: string, question:InspectorReportQuestionModel)
    {
        if(question)
        {
            const originalQuestion = this._originalSection.questions.find(x=>x['id'] == question['id']);
            
            // if comments are the same as original revert back the UOW changes to original. Otherwise it's treated as a change. 
            if(originalQuestion && originalQuestion.comments == value ){
                question.isOverriden = originalQuestion.isOverriden;
                if(question.rightshipInspectionAnswerClassificationId == RightshipInspectionAnswerClassificationsEnum.No){
                    question.rightShipInspectionDeficiency.comment = originalQuestion.rightShipInspectionDeficiency.comment;
                }
                else{
                    question.comments = originalQuestion.comments;       
                }     
            } else{
                if(question.rightshipInspectionAnswerClassificationId == RightshipInspectionAnswerClassificationsEnum.No)
                {
                    question.rightShipInspectionDeficiency.comment = value;
                }
                this.updateOverridenFlag(question);
            }

           
        }
    }

    cancel()
    {
        this.dialogClientService.hide(true);
    }
}
